import { html, PropertyValueMap, TemplateResult, CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { SlotController } from '../../controllers/slot-controller';
import { MOBILE_BREAK_POINT } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import { BlocksListItem } from '../list-item/list-item';
import { BlocksList } from '../list/list';
import { lockBodyScrolling, unlockBodyScrolling } from '../../util/scroll';
import { BlocksSideMenuItem } from '../side-menu-item/side-menu-item';
import styles from './side-menu.styles';

/**
 * @deprecated - This component `cat-side-menu` has been deprecated and the updated version will be available with name `cat-side-menu-labs`. New component will be found under labs folder from Minor Release 3.10.0, `cat-side-menu` will be removed in 5.0 major release.
 * @slot default - The component content
 * @slot header - The header content
 * @slot footer - The header content
 * @csspart base - The component's base wrapper.
 * @csspart header-wrap - The component's header wrapper.
 * @csspart part-header - The component's header slot.
 * @csspart nav - The component's nav element.
 * @csspart list - The component's list.
 * @csspart part-default - The component's default slot.
 * @csspart footer-wrap - The component's footer wrapper.
 * @csspart part-footer - The component's footer slot.
 */
export class BlocksSideMenu extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * is Active
   * 1) Panel is open when set to true. Close when set to false
   */
  @property({ type: Boolean })
  isCollapsed: boolean | undefined;

  /**
   * aria-label attribute
   * Provides an accessible name for the sideNav element
   */
  @property({ type: String })
  arialabel = 'Side menu';

  /**
   * Breakpoint below which side-menu switches to mobile view. Defaults to 768px.
   */
  @property({ type: Number })
  mobileBreakPoint = MOBILE_BREAK_POINT;

  @state()
  private isMobile = false;

  protected slots = new SlotController(this, 'header', 'footer', null);

  /**
   * Query the sideNavItems from light dom
   */
  private get sideMenuItems(): BlocksSideMenuItem[] {
    return Array.from(this.querySelectorAll('cat-side-menu-item'));
  }
  
  /**
   * Returns closest first list-item/side-menu-item from list/side-menu.
   */
  private closestListItem = (menuItem: HTMLElement) => {
    const list = menuItem?.closest('cat-list');
    if (!list) {
      return;
    }
    return list.closest('cat-list-item') ? list.closest('cat-list-item') : list.closest('cat-side-menu-item');
  };

  /**
   * Connected callback lifecycle
   * 1) Add window resize event listener
   */
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.onResize); /* 1 */
  }

  /**
   * Disconnected callback lifecycle
   * 1) Remove window resize event listener
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.onResize); /* 1 */
    unlockBodyScrolling(this) //Remove body scroll lock when disconnected
  }

  /**
   * Resize Handler
   */
  private onResize = () => {
    if (window.innerWidth < this.mobileBreakPoint) {
      this.isMobile = true;
      if (this.isCollapsed === false) {
        lockBodyScrolling(this, 'auto')
      } else {
        unlockBodyScrolling(this)
      }
    } else {
      this.isMobile = false;
      unlockBodyScrolling(this)
    }
    this.sideMenuItems.forEach((sideMenuItem: BlocksSideMenuItem) => {
      sideMenuItem.isMobile = this.isMobile;
    });
  };

  /**
   * First updated lifecycle when component is first updated on page
   */
  protected firstUpdated(): void {
    this.onResize();
  }
  /**
   * When component is updated
   * 1) If the side menu is collapsed, turn the `isCollapsed` property on on the items
   * 2) Otherwise, turn `isCollapsed` off
   * 3) If mobile is detected, body overflow should be hidden on expanded mode.
   */
  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('isCollapsed')) {
      this.onResize(); /* 3 */
      const sideMenuItems = this.querySelectorAll('cat-side-menu-item');
      sideMenuItems.forEach((element: BlocksSideMenuItem) => {
        if (this.isCollapsed === true) {
          element.isCollapsed = true; /* 1 */
          if (element.isActive === true) element.isActive = false;
        } else {
          element.isCollapsed = false; /* 2 */
        }
      });
    }
  }

  /**
   * 1. This will set role for the side menu items for tree view
   */
  private setRoles() {
    const list = Array.from(this.querySelectorAll('cat-list'));
    list.forEach((item: BlocksList) => {
      item.listRole = 'group';
    });

    const listItem = Array.from(this.querySelectorAll('cat-list-item'));
    listItem.forEach((item: BlocksListItem) => {
      item.listItemRole = 'treeitem';
    });

    const sideMenuItem = Array.from(this.querySelectorAll('cat-side-menu-item'));
    sideMenuItem.forEach((item: BlocksSideMenuItem) => {
      item.sideMenuItemRole = 'treeitem';
    });
  }

  /**
   * Handle the keydown event for the side menu
   * 1) If the key is arrow down, increment the current index then focus on the next item.
   *  If the key is arrow up, decrement the current index then focus on the previous item
   * 2) If the key is Home, focus the first item. If the key is End, focus the last item.
   * 3) If the key is Arrow Right, check expand the item if not already expanded based on isActive; if already expanded, focus the next item.
   * 4) If the key is Arrow Left, collapse the item if expanded based on isActive; if already collapsed, focus the parent item containing the list.
   * 5) Get the focusable item and focus on it.
   */
  private handleSideNavKeyDown(event: KeyboardEvent) {
    const allElements = this.getAllElements();
    const currentSelectedItem = this.getFocusedMenuItem();
    const isSideMenuItem = currentSelectedItem?.tagName?.toLowerCase() === 'cat-side-menu-item';
    const isListItem = currentSelectedItem?.tagName?.toLowerCase() === 'cat-list-item';

    let newFocusItem: HTMLElement | null = null;

    if (event.key === KEYS.DOWN_ARROW || event.key === KEYS.UP_ARROW) {
      /*1*/
      newFocusItem = this.getNewFocusItem(event, allElements, currentSelectedItem);
    } else if (event.key === KEYS.HOME) {
      /*2*/
      newFocusItem = allElements[0];
    } else if (event.key === KEYS.END) {
      /*2*/
      newFocusItem = allElements[allElements.length - 1];
    } else if (event.key === KEYS.RIGHT_ARROW) {
      /*3*/
      let focusedItem = currentSelectedItem as BlocksSideMenuItem | BlocksListItem;

      if (focusedItem.slots.hasSlotted('items')) {
        if (isSideMenuItem) {
          this.getSideMenuItems().forEach((menuItem) => {
            if (menuItem !== currentSelectedItem && !menuItem.isDisabled) menuItem.isActive = false;
          });
        }
        if ((isSideMenuItem && !(focusedItem as BlocksSideMenuItem)?.isDisabled) || (isListItem && !(focusedItem as BlocksListItem)?.disabled)) {
          if (focusedItem.isActive) {
            newFocusItem = this.getNewFocusItem(event, allElements, currentSelectedItem);
          } else {
            focusedItem.isActive = true;
          }
        }
      }
    } else if (event.key === KEYS.LEFT_ARROW) {
      /*4*/
      let focusedItem = currentSelectedItem as BlocksSideMenuItem | BlocksListItem;

      if (focusedItem.slots.hasSlotted('items') && focusedItem.isActive) {
        focusedItem.isActive = false;
      } else {
        newFocusItem = this.closestListItem(focusedItem) as HTMLElement;
      }
    }

    /*5*/
    newFocusItem = this.getFocusableItem(newFocusItem);

    newFocusItem?.focus();
  }

  /**
   * Function to get the next focusable item in the list.
   * If the DOWN_ARROW or RIGHT_ARROW key is pressed, it increments the index.
   * If the UP_ARROW is pressed, it decrements the index.
   * The index wraps around if it goes out of bounds.
   *
   * @param event - The keyboard event triggering the focus change.
   * @param allElements - An array of all focusable elements.
   * @param currentSelectedItem - The currently selected item.
   * @returns The next focusable item in the list.
   */

  private getNewFocusItem = (event: KeyboardEvent, allElements: HTMLElement[], currentSelectedItem: HTMLElement | null) => {
    event.preventDefault();
    const currentIndex = allElements.findIndex((item) => item === currentSelectedItem);
    if (event.key === KEYS.DOWN_ARROW || event.key === KEYS.RIGHT_ARROW) {
      return allElements[(currentIndex + 1) % allElements.length];
    } else {
      return allElements[(currentIndex - 1 + allElements.length) % allElements.length];
    }
  };

  /*
   * Get the focusable item
   */
  private getFocusableItem(newFocusItem: HTMLElement | null) {
    if (newFocusItem?.tagName?.toLowerCase() === 'cat-side-menu-item') {
      newFocusItem = newFocusItem.shadowRoot?.querySelector('.cat-c-side-menu__link') as HTMLElement;
    } else {
      const firstFocusableElement = newFocusItem?.shadowRoot?.querySelector('.cat-c-list-item__link') as HTMLElement;
      newFocusItem = firstFocusableElement;
    }
    return newFocusItem;
  }

  /*
   * Get all the elements in the side menu and all sub list items
   */
  private getAllElements() {
    let allElements = this.getSideMenuItems() as HTMLElement[];

    const currentActiveSideMenu = this.getSideMenuItems().find((item) => item.isActive);

    if (allElements && currentActiveSideMenu) {
      const list = currentActiveSideMenu.querySelector('cat-list');
      const index = allElements.findIndex((item) => item === currentActiveSideMenu);
      if (list) {
        let listItems = [] as BlocksListItem[];
        listItems = this.populateListItems(list);

        allElements.splice(index + 1, 0, ...(Array.from(listItems) as HTMLElement[]));
      }
    }

    return allElements;
  }

  /*
   * Get all the list items in the list
   */
  private populateListItems(list: BlocksList) {
    const listItems = [] as BlocksListItem[];
    Array.from(list.children).forEach((item) => {
      const lisItem = item as BlocksListItem;
      if (!lisItem.isStatic) {
        if (lisItem.isActive) {
          const list = lisItem.querySelector('cat-list');
          if (list) {
            listItems.push(lisItem);
            listItems.push(...this.populateListItems(list));
          }
        } else {
          listItems.push(lisItem);
        }
      }
    });
    return listItems;
  }

  /*
   * Get the side menu items
   */
  private getSideMenuItems() {
    return Array.from(this.querySelectorAll('cat-side-menu-item')!);
  }

  /*
   * Get the focused menu item
   */
  private getFocusedMenuItem(): HTMLElement | null {
    const activeElement = this.getActiveElement(document);
    return activeElement instanceof HTMLElement ? activeElement : null;
  }

  /**
   * Get the deeper active element
   */
  private getActiveElement(document: Document | ShadowRoot, activeElement?: Element): Element | null | undefined {
    if (document.activeElement?.shadowRoot?.activeElement) {
      return this.getActiveElement(document.activeElement.shadowRoot, document.activeElement);
    }
    return activeElement;
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-side-menu', {
      'cat-is-collapsed': this.isCollapsed === true,
      'cat-is-mobile': this.isMobile === true,
      'cat-c-side-menu--has-header-slot': this.slots.hasSlotted('header'),
      'cat-c-side-menu--has-footer-slot': this.slots.hasSlotted('footer')
    });

    return html`
      <div part="base" class="${componentClassNames}">
        <header part="header-wrap" class="cat-c-side-menu__header">
          <slot part="part-header" name="header"></slot>
        </header>
        <nav part="nav" class="cat-c-side-menu__nav">
          <ul aria-label=${this.arialabel} part="list" role="tree" class="cat-c-side-menu__list" @keydown=${this.handleSideNavKeyDown}>
            <slot part="part-default" @slotchange=${this.setRoles}></slot>
          </ul>
        </nav>
        <footer part="footer-wrap" class="cat-c-side-menu__footer">
          <slot part="part-footer" name="footer"></slot>
        </footer>
      </div>
    `;
  }
}

if (customElements.get('cat-side-menu') === undefined) {
  customElements.define('cat-side-menu', BlocksSideMenu);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-side-menu': BlocksSideMenu;
  }
}
